import React, { useState, useMemo } from 'react';
import { useGetAlarmLogQuery, useAcknowledgeAlarmLogMutation } from '../services/alarmApi';
import { useGetSensorListQuery } from '../services/sensorApi';
import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';

function getWorstValue(logs: any[], alarmType: string) {
  if (alarmType === 'high') {
    return Math.max(...logs.map(l => Number(l.alarm_values)));
  } else if (alarmType === 'low') {
    return Math.min(...logs.map(l => Number(l.alarm_values)));
  }
  return '-';
}

function formatDuration(ms: number) {
  if (isNaN(ms) || ms < 0) return '-';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'unack', label: 'Unacknowledged First' },
];

export default function AlarmLogPage() {
  const { data: alarmLog, isLoading, isError, error, refetch } = useGetAlarmLogQuery({ includeAcknowledged: true });
  const { data: sensors } = useGetSensorListQuery();
  const [sortBy, setSortBy] = useState('newest');
  const user = useSelector((state: RootState) => state.auth.user);
  const [acknowledgeAlarmLog] = useAcknowledgeAlarmLogMutation();
  const [ackRow, setAckRow] = useState<number | null>(null);
  const [ackSuccessRows, setAckSuccessRows] = useState<{ [key: number]: boolean }>({});
  const [editNoteLogId, setEditNoteLogId] = useState<number | null>(null);
  const [noteInput, setNoteInput] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  const getSensorInfo = (sensorId: number) => {
    if (!sensors) return { name: '-', serialNo: '-' };
    const sensor = sensors.find((s: any) => s.id === sensorId);
    return sensor ? { name: sensor.name, serialNo: sensor.serialNo } : { name: '-', serialNo: '-' };
  };

  // Group by event_id to show one entry per alarm event.
  const events = useMemo(() => {
    if (!Array.isArray(alarmLog)) {
      return [];
    }
    const eventMap = new Map();

    // Group all logs by event_id
    for (const log of alarmLog) {
      const eventId = log.event_id || `log-${log.idAlarmLog}`;
      if (!eventMap.has(eventId)) {
        eventMap.set(eventId, []);
      }
      eventMap.get(eventId).push(log);
    }

    // Create a summary for each event
    return Array.from(eventMap.values()).map((eventLogs: any[]) => {
      // Sort logs by time to reliably find the first one
      eventLogs.sort((a, b) => new Date(a.activated).getTime() - new Date(b.activated).getTime());
      
      const initialLog = eventLogs[0];
      const resetLog = eventLogs.find(l => l.alarm_reset);
      
      const isAcknowledged = eventLogs.every(l => l.acknowledged);
      // Find the last person who acknowledged any part of the event.
      const lastAcknowledgedLog = [...eventLogs].reverse().find(l => l.acknowledged_by);

      return {
        ...initialLog,
        // Ensure key properties are from the initial log
        idAlarmLog: initialLog.idAlarmLog,
        event_id: initialLog.event_id,
        // Summarize properties
        alarm_reset: resetLog ? resetLog.alarm_reset : null,
        alarm_values: getWorstValue(eventLogs, initialLog.alarm_type),
        acknowledged: isAcknowledged,
        acknowledged_by: isAcknowledged ? (lastAcknowledgedLog?.acknowledged_by || 'Yes') : null,
        notes: initialLog.notes, // Or maybe summarize notes? For now, first is fine.
      };
    });
  }, [alarmLog]);

  // Sorting logic
  let sortedEvents = [...events];
  if (sortBy === 'newest') {
    sortedEvents.sort((a, b) => (b.activated ? new Date(b.activated).getTime() : 0) - (a.activated ? new Date(a.activated).getTime() : 0));
  } else if (sortBy === 'oldest') {
    sortedEvents.sort((a, b) => (a.activated ? new Date(a.activated).getTime() : 0) - (b.activated ? new Date(b.activated).getTime() : 0));
  } else if (sortBy === 'unack') {
    sortedEvents.sort((a, b) => {
      if (a.acknowledged_by === b.acknowledged_by) {
        return (b.activated ? new Date(b.activated).getTime() : 0) - (a.activated ? new Date(a.activated).getTime() : 0);
      }
      return a.acknowledged_by ? 1 : -1;
    });
  }

  // Handler for acknowledging alarm event (acknowledge all alarms with a matching event_id)
  const handleAcknowledge = async (logId: number) => {
    if (!user?.username) return;
    if (!logId || isNaN(Number(logId))) {
      console.error('Invalid logId for acknowledge:', logId);
      return;
    }
    // Find the log being acknowledged
    const log = events.find(l => l.idAlarmLog === logId);
    if (!log || !log.event_id) {
      console.error('Could not find log or event_id for acknowledgment for logId:', logId);
      return;
    }
    setAckRow(logId);
    try {
      // Find all logs with same event_id
      const allLogs = Array.isArray(alarmLog) ? alarmLog : [];
      const logsToAcknowledge = allLogs.filter(l =>
        l.event_id === log.event_id && !l.acknowledged
      );

      await Promise.all(
        logsToAcknowledge.map(l =>
          acknowledgeAlarmLog({
            logId: l.idAlarmLog,
            body: {
              acknowledged: true,
              acknowledged_by: user.username,
              alarm_reset: new Date().toISOString()
            }
          })
        )
      );
      setAckSuccessRows(prev => ({ ...prev, [logId]: true }));
      refetch();
    } catch (e) {
      console.error('Acknowledge error:', e);
    } finally {
      setAckRow(null);
    }
  };

  const handleEditNote = (logId: number, currentNote: string) => {
    setEditNoteLogId(logId);
    setNoteInput(currentNote || '');
  };

  const handleSaveNote = async (logId: number) => {
    if (!user?.username) return;
    setIsSavingNote(true);
    try {
      // Append username to note
      const noteWithUser = noteInput.trim() ? `${noteInput.trim()} —${user.username}` : '';
      await acknowledgeAlarmLog({ logId, body: { notes: noteWithUser } });
      setEditNoteLogId(null);
      setNoteInput('');
      refetch();
    } catch (e) {
      console.error('Save note error:', e);
    } finally {
      setIsSavingNote(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Alarm Log</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Inspect historical alarms for all sensors
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="sortBy" className="text-xs font-medium text-gray-700 dark:text-gray-300">Sort by:</label>
            <select
              id="sortBy"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="rounded border-gray-300 dark:bg-gray-800 dark:text-white text-xs px-2 py-1"
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {isLoading && <div className="text-gray-700 dark:text-gray-200">Loading alarm log...</div>}
      {isError && (
        <div className="text-red-600 dark:text-red-400">
          Error loading alarm log: {typeof error === 'object' && error && 'status' in error ? JSON.stringify(error) : (error as any)?.message || 'Unknown error'}
        </div>
      )}
      {sortedEvents.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-white">
            <thead className="bg-gray-50 dark:bg-gray-900 text-white">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Sensor Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Serial No</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Alarm Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Alarm Start Time</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Alarm End Time</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Duration</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Acknowledged By</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Worst Value</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Notes</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 text-white">
              {sortedEvents.map((log, idx) => (
                <tr key={log.idAlarmLog || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700 text-white">
                  <td className="px-4 py-2 whitespace-nowrap text-white">{getSensorInfo(log.sensor_id).name}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-white">{getSensorInfo(log.sensor_id).serialNo}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{log.alarm_type}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{log.activated ? new Date(log.activated).toLocaleString() : '-'}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{log.alarm_reset ? new Date(log.alarm_reset).toLocaleString() : '-'}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{/* Duration */}
                    {log.activated && log.alarm_reset ? formatDuration(new Date(log.alarm_reset).getTime() - new Date(log.activated).getTime()) : '-'}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{log.acknowledged_by || '-'}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{log.alarm_values}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="truncate max-w-[120px]">{log.notes || <span className="text-gray-400">No note</span>}</span>
                      <button
                        className="text-blue-400 hover:text-blue-600 text-xs underline"
                        onClick={() => handleEditNote(log.idAlarmLog, log.notes)}
                        title="Edit note"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {!log.acknowledged ? (
                      <button
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-semibold disabled:opacity-60"
                        onClick={() => handleAcknowledge(log.idAlarmLog)}
                        disabled={ackRow === log.idAlarmLog || ackSuccessRows[log.idAlarmLog]}
                      >
                        {ackRow === log.idAlarmLog
                          ? 'Acknowledging...'
                          : ackSuccessRows[log.idAlarmLog]
                          ? 'Acknowledged!'
                          : 'Acknowledge'}
                      </button>
                    ) : (
                      <span className="text-green-400 text-xs">Acknowledged</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sortedEvents.length === 0 && !isLoading && (
        <div className="text-gray-700 dark:text-gray-200">No alarm events found.</div>
      )}
      {editNoteLogId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">Edit Note</h3>
            <textarea
              className="w-full h-28 p-2 border border-gray-300 dark:border-gray-700 rounded mb-4 bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white"
              value={noteInput}
              onChange={e => setNoteInput(e.target.value)}
              placeholder="Enter note..."
              disabled={isSavingNote}
            />
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1 rounded bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-white"
                onClick={() => setEditNoteLogId(null)}
                disabled={isSavingNote}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 rounded bg-blue-600 text-white font-semibold disabled:opacity-60"
                onClick={() => handleSaveNote(editNoteLogId)}
                disabled={isSavingNote}
              >
                {isSavingNote ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 